import type { ReactElement } from 'react';

import { LegalDoc } from '@/widgets/legal-doc';
import { COMPANY } from '@/shared/config';

const EFFECTIVE = '2026년 9월 1일';

/**
 * 이용약관 — 1차 오픈 범위(14 §8) 기준.
 *
 * **결제·청약철회 조항을 뺐다.** 현재 유료 서비스가 없고 통신판매업 신고도 전이다.
 * 팔지 않는데 결제 조항을 두면 푸터에 통신판매업자라고 적는 것과 같은 문제가 된다 —
 * 사실과 다른 표시다. 대신 §10 에 「유료 서비스 도입 시 별도 고지 후 추가」를 명시했다.
 */
export function TermsPage(): ReactElement {
  return (
    <LegalDoc
      title="이용약관"
      effectiveDate={EFFECTIVE}
      articles={[
        {
          id: 'purpose',
          title: '목적',
          body: (
            <p>
              이 약관은 {COMPANY.legalName}(이하 &ldquo;회사&rdquo;)가 운영하는{' '}
              {COMPANY.serviceName}(이하 &ldquo;서비스&rdquo;)의 이용 조건과 절차, 회사와 회원의
              권리·의무를 정하는 것을 목적으로 합니다.
            </p>
          ),
        },
        {
          id: 'definition',
          title: '용어의 정의',
          body: (
            <ul className="flex list-disc flex-col gap-1 pl-5">
              <li>
                <strong>회원</strong> — 이 약관에 동의하고 계정을 만들어 서비스를 이용하는 사람
              </li>
              <li>
                <strong>자료</strong> — 회사가 서비스에서 제공하는 템플릿·문서 등 디지털 콘텐츠
              </li>
              <li>
                <strong>계정</strong> — 회원을 식별하기 위해 회원이 등록한 이메일 주소 및 이에
                연결된 소셜 계정
              </li>
            </ul>
          ),
        },
        {
          id: 'posting',
          title: '약관의 게시와 개정',
          body: (
            <>
              <p>회사는 이 약관을 서비스 초기화면에서 확인할 수 있도록 게시합니다.</p>
              <p>
                회사는 관련 법령을 위반하지 않는 범위에서 약관을 개정할 수 있습니다. 개정할 때는
                적용일과 개정 사유를 명시해 <strong>적용일 7일 전부터</strong> 공지합니다. 다만
                회원에게 불리한 개정은 <strong>30일 전부터</strong> 공지하고, 회원이 개정 약관에
                동의하지 않으면 탈퇴할 수 있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'contract',
          title: '이용계약의 성립',
          body: (
            <>
              <p>
                이용계약은 이용자가 이 약관과 개인정보 수집·이용에 동의하고 회사가 이를 승낙함으로써
                성립합니다.
              </p>
              <p>
                <strong>만 14세 미만은 가입할 수 없습니다.</strong> 회사는 법정대리인의 동의를 받는
                절차를 두고 있지 않으므로, 가입 시 만 14세 이상임을 확인합니다.
              </p>
              <p>
                회사는 다음의 경우 이용 신청을 승낙하지 않거나 사후에 이용계약을 해지할 수 있습니다
                — 타인의 정보를 도용한 경우, 허위 정보를 기재한 경우, 이전에 이 약관 위반으로 이용이
                제한된 적이 있는 경우.
              </p>
            </>
          ),
        },
        {
          id: 'account',
          title: '계정 관리',
          body: (
            <>
              <p>
                회원은 이메일 또는 회사가 정한 소셜 로그인(카카오·네이버)으로 계정을 만들 수
                있습니다.
              </p>
              <p>
                계정 관리 책임은 회원에게 있습니다. 회원은 계정을 타인에게 양도하거나 빌려줄 수
                없으며, 계정이 도용된 사실을 알게 되면 즉시 회사에 알리고 안내에 따라야 합니다.
              </p>
            </>
          ),
        },
        {
          id: 'assets',
          title: '자료의 이용 범위',
          body: (
            <>
              <p>
                회사가 제공하는 자료는 <strong>회원 본인의 업무·학습 목적</strong>으로 이용할 수
                있습니다. 다운로드한 자료를 회원이 수정해 자신의 업무에 사용하는 것은 제한하지
                않습니다.
              </p>
              <p>다만 다음 행위는 회사의 사전 서면 동의 없이 할 수 없습니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>자료 원본 또는 실질적으로 동일한 형태를 재배포·판매·대여하는 행위</li>
                <li>자료를 공개된 곳에 게시해 불특정 다수가 내려받게 하는 행위</li>
                <li>자료를 이용해 동일하거나 유사한 서비스를 제공하는 행위</li>
              </ul>
              <p>
                자료는 <strong>있는 그대로</strong> 제공됩니다. 회사는 자료가 회원의 특정 업무
                환경에 적합하다거나 특정한 결과를 보장하지 않습니다.
              </p>
            </>
          ),
        },
        {
          id: 'obligation',
          title: '회원의 의무',
          body: (
            <>
              <p>회원은 다음 행위를 해서는 안 됩니다.</p>
              <ul className="flex list-disc flex-col gap-1 pl-5">
                <li>가입·문의 시 허위 정보를 기재하는 행위</li>
                <li>타인의 계정·개인정보를 도용하는 행위</li>
                <li>서비스의 정상적인 운영을 방해하는 행위(자동화된 대량 요청 등)</li>
                <li>회사 또는 제3자의 지식재산권·명예를 침해하는 행위</li>
              </ul>
            </>
          ),
        },
        {
          id: 'suspension',
          title: '서비스의 제공과 중단',
          body: (
            <>
              <p>
                서비스는 연중무휴 24시간 제공을 원칙으로 합니다. 다만 설비 점검·교체, 장애, 천재지변
                등이 있을 때 서비스의 전부 또는 일부를 중단할 수 있습니다.
              </p>
              <p>
                예정된 점검은 사전에 공지합니다. 예측할 수 없는 사유로 중단된 경우에는 사후에 공지할
                수 있습니다.
              </p>
              <p>
                회사는 서비스의 내용을 변경하거나 종료할 수 있으며, 종료할 때는{' '}
                <strong>30일 전에 공지</strong>합니다.
              </p>
            </>
          ),
        },
        {
          id: 'closure',
          title: '탈퇴와 이용 제한',
          body: (
            <>
              <p>회원은 언제든지 탈퇴할 수 있으며, 회사는 지체 없이 처리합니다.</p>
              <p>
                회원이 이 약관을 위반하면 회사는 사전 통지 후 이용을 제한할 수 있습니다. 다만 타인의
                권리를 침해하거나 서비스 운영을 심각하게 방해하는 경우에는 즉시 제한하고 사후에
                통지할 수 있습니다.
              </p>
            </>
          ),
        },
        {
          id: 'ip',
          title: '지식재산권',
          body: (
            <>
              <p>서비스와 자료에 대한 저작권 등 지식재산권은 회사에 있습니다.</p>
              <p>
                회원이 문의 과정에서 제출한 내용에 대한 권리는 회원에게 있습니다. 회사는 문의 응대와
                제안을 위해서만 이를 이용합니다.
              </p>
            </>
          ),
        },
        {
          id: 'paid',
          title: '유료 서비스',
          body: (
            <>
              <p>
                <strong>현재 서비스에는 유료 판매·결제 기능이 없습니다.</strong> 자료는 무료로
                제공되며, 교육은 별도 문의를 통해 개별 계약으로 진행됩니다.
              </p>
              <p>
                회사가 유료 서비스를 도입할 경우 결제·청약철회·환불에 관한 조항을 이 약관에
                추가하고, 적용일 전에 이용자에게 고지합니다.
              </p>
            </>
          ),
        },
        {
          id: 'liability',
          title: '책임의 제한',
          body: (
            <>
              <p>
                회사는 천재지변, 통신망 장애 등 회사의 통제를 벗어난 사유로 서비스를 제공하지 못한
                경우 책임을 지지 않습니다.
              </p>
              <p>
                회사는 회원이 자료를 이용해 얻은 결과나, 자료를 이용하는 과정에서 회원이 사용한
                제3자 도구·서비스에서 발생한 문제에 대해 책임을 지지 않습니다.
              </p>
              <p>회사의 고의 또는 중대한 과실로 발생한 손해에 대해서는 그러하지 않습니다.</p>
            </>
          ),
        },
        {
          id: 'dispute',
          title: '분쟁 해결',
          body: (
            <>
              <p>
                회사와 회원 사이에 분쟁이 생기면 협의로 해결하는 것을 원칙으로 합니다. 협의가
                이루어지지 않으면 관계 법령이 정한 절차에 따릅니다.
              </p>
              <p>이 약관은 대한민국 법을 따릅니다.</p>
            </>
          ),
        },
        {
          id: 'contact',
          title: '문의처',
          body: (
            <>
              <p>
                {COMPANY.legalName} · 사업자등록번호 {COMPANY.businessNumber}
              </p>
              <p>{COMPANY.address}</p>
              <p>{COMPANY.email}</p>
            </>
          ),
        },
      ]}
    />
  );
}
